/**
 * ScanDebugPanel — Collapsible debug log for barcode scanner pipeline.
 * Shows last 20 scan entries with parsed GS1 fields and results.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, Bug } from 'lucide-react';

export type ScanLogResult = 'success' | 'error' | 'waiting' | 'merged' | 'processing';

export interface ScanLogEntry {
  id: number;
  timestamp: Date;
  rawBarcode: string;
  gtin: string | null;
  refCode: string | null;
  lote: string | null;
  validade: string | null;
  result: ScanLogResult;
  message: string;
}

interface Props {
  logs: ScanLogEntry[];
  onClear: () => void;
}

const RESULT_COLORS: Record<ScanLogResult, string> = {
  success: 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
  error: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30',
  waiting: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30',
  merged: 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30',
  processing: 'text-gray-700 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/30',
};

const RESULT_LABELS: Record<ScanLogResult, string> = {
  success: 'OK',
  error: 'ERRO',
  waiting: 'AGUARD.',
  merged: 'MERGE',
  processing: '...',
};

function formatTime(d: Date): string {
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.substring(0, max) + '...' : s;
}

export const ScanDebugPanel: React.FC<Props> = ({ logs, onClear }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Bug className="w-4 h-4 text-gray-500" />
        <span className="font-medium text-gray-700 dark:text-gray-300">
          Debug Scanner
        </span>
        {logs.length > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({logs.length} {logs.length === 1 ? 'entrada' : 'entradas'})
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {logs.length > 0 && (
            <div className="flex justify-end px-3 py-1 bg-gray-50 dark:bg-gray-800">
              <button
                onClick={onClear}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
                Limpar
              </button>
            </div>
          )}

          {logs.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 p-3 text-center">
              Nenhuma leitura registrada
            </p>
          ) : (
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="px-2 py-1.5 font-medium">Hora</th>
                    <th className="px-2 py-1.5 font-medium">Raw</th>
                    <th className="px-2 py-1.5 font-medium">GTIN</th>
                    <th className="px-2 py-1.5 font-medium">RefCode</th>
                    <th className="px-2 py-1.5 font-medium">Lote</th>
                    <th className="px-2 py-1.5 font-medium">Val</th>
                    <th className="px-2 py-1.5 font-medium">Resultado</th>
                    <th className="px-2 py-1.5 font-medium">Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr
                      key={log.id}
                      className="border-t border-gray-100 dark:border-gray-800"
                    >
                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatTime(log.timestamp)}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300 max-w-[120px] truncate" title={log.rawBarcode}>
                        {truncate(log.rawBarcode, 20)}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">
                        {log.gtin ? truncate(log.gtin, 14) : '—'}
                      </td>
                      <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">
                        {log.refCode || '—'}
                      </td>
                      <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                        {log.lote || '—'}
                      </td>
                      <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                        {log.validade || '—'}
                      </td>
                      <td className="px-2 py-1">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${RESULT_COLORS[log.result]}`}>
                          {RESULT_LABELS[log.result]}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={log.message}>
                        {truncate(log.message, 40)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
